import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { humanReadableDate } from "../../../helper/dateTimeHelper";
import { toProperCase } from "../../../helper/textHelper";

interface Props {
  filteredData: any[];
  handleProductEdit: any;
  setDeleteRecord: any;
  setSelectedLostProduct: any;
}

const AssetProductGrid: React.FC<Props> = ({
  filteredData,
  handleProductEdit,
  setDeleteRecord,
  setSelectedLostProduct,
}) => {
  // Pagination state
  const defaultPageSize = 8;
  const defaultPageNumber = 1;
  const [currentPage, setCurrentPage] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Calculate pagination
  const totalItems = filteredData?.length || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  // Get current page data
  const currentData = useMemo(() => {
    return filteredData?.slice(startIndex, endIndex) || [];
  }, [filteredData, startIndex, endIndex]);

  // Reset to first page when filtered data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Generate page numbers with Ant Design logic
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { class: string; icon: string }> = {
      Active: { class: "border-success", icon: "ti-check-circle" },
      Inactive: { class: "border-danger", icon: "ti-circle-x" },
      Pending: { class: "border-warning", icon: "ti-clock" },
      Lost: { class: "border-warning", icon: "ti-alert-triangle" },
    };
    return (
      statusConfig[status] || {
        class: "badge-secondary",
        icon: "ti-help-circle",
      }
    );
  };

  const getWarrantyStatus = (warrantyDate: string) => {
    if (!warrantyDate) {
      return {
        class: "text-muted",
        text: "N/A",
        icon: "ti-help-circle",
      };
    }

    const isExpired = new Date(warrantyDate) < new Date();
    const daysLeft = Math.ceil(
      (new Date(warrantyDate).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );

    if (isExpired) {
      return {
        class: "text-danger",
        text: "Expired",
        icon: "ti-alert-triangle",
      };
    } else if (daysLeft <= 30) {
      return {
        class: "text-warning",
        text: `${daysLeft} days left`,
        icon: "ti-clock-alert",
      };
    } else {
      return { class: "text-success", text: "Valid", icon: "ti-shield-check" };
    }
  };

  return (
    <>
      {/* Product Grid */}
      <div className="row mt-3 z-0">
        {currentData &&
          currentData.map((record, index) => {
            const statusConfig = getStatusBadge(record?.status);
            const warrantyStatus = getWarrantyStatus(record?.warranty);

            return (
              <div
                key={record.id || index}
                className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
                <div className="card product-card shadow-md border-1">
                  {/* Status Dot */}
                  <span
                    className={`avatar-xl avatar-rounded online border p-1 ${
                      record?.status?.toLowerCase() === "active"
                        ? "bg-success border-success"
                        : record?.status?.toLowerCase() === "lost"
                        ? "bg-warning border-warning"
                        : "bg-danger border-danger"
                    } rounded-circle position-absolute mt-3 mx-3 top-0 start-0`}></span>

                  {/* Product Image */}
                  <div className="text-center p-2">
                    <div className="product-image-container mb-2">
                      <Link to="" className="d-inline-block position-relative">
                        <div className="avatar avatar-xxl rounded-2 bg-light">
                          <ImageWithBasePath
                            src={
                              !record?.image_url_object?.url ||
                              record?.image_url_object?.url === "null"
                                ? "assets/img/products/product-icon.png"
                                : record?.image_url_object?.url
                            }
                            alt={record?.name}
                            className="w-100 h-100 object-fit-cover rounded-3"
                          />
                        </div>
                      </Link>
                    </div>

                    {/* Product Details */}
                    <div className="product-details">
                      <Link to="" className="text-decoration-none">
                        <h6
                          className="card-title mb-1 text-dark fw-semibold"
                          title={record?.name}>
                          {toProperCase(
                            record?.name?.length > 25
                              ? `${record?.name.substring(0, 25)}...`
                              : record?.name
                          )}
                        </h6>
                      </Link>

                      {/* Category Breadcrumb */}
                      <div className="text-muted small mb-2">
                        <span className="badge bg-secondary-subtle text-secondary-emphasis border-0 small">
                          {record?.category?.breadcrumb || "Uncategorized"}
                        </span>
                      </div>

                      <div className="text-muted small mb-2">
                        <span className="badge bg-light mx-1">
                          {record?.brand}
                        </span>
                        <span>{toProperCase(record?.model)}</span>
                      </div>

                      {/* Key Information Grid */}
                      <div className="row g-2 text-start">
                        <div className="col-12">
                          <div className="d-flex align-items-center justify-content-between py-1">
                            <span className="text-muted small">
                              <i className="ti ti-id me-1"></i>Serial ID
                            </span>
                            <span className="fw-medium small">
                              {record?.serial_no}
                            </span>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="d-flex align-items-center justify-content-between py-1">
                            <span className="text-muted small">
                              <i className="ti ti-building-store me-1"></i>
                              Vendor
                            </span>
                            <span
                              className="fw-medium small text-truncate"
                              style={{ maxWidth: "120px" }}
                              title={record?.purchase_from}>
                              {toProperCase(record?.purchase_from) || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="card-footer bg-light border-0 p-3">
                    <div className="row g-2">
                      {/* Purchase Date */}
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-muted small mb-1">
                            <i className="ti ti-calendar-event text-info me-1"></i>
                            Purchased
                          </div>
                          <span className="badge bg-info-subtle text-info-emphasis border-0 small">
                            {record?.purchase_date
                              ? humanReadableDate(record?.purchase_date)
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Warranty Status */}
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-muted small mb-1">
                            <i
                              className={`ti ${warrantyStatus.icon} ${warrantyStatus.class} me-1`}></i>
                            Warranty
                          </div>
                          <span
                            className={`badge ${
                              warrantyStatus.class === "text-danger"
                                ? "bg-danger-subtle text-danger-emphasis"
                                : warrantyStatus.class === "text-warning"
                                ? "bg-warning-subtle text-warning-emphasis"
                                : warrantyStatus.class === "text-success"
                                ? "bg-success-subtle text-success-emphasis"
                                : "bg-secondary-subtle text-secondary-emphasis"
                            } border-0 small`}>
                            {record?.warranty
                              ? humanReadableDate(record?.warranty)
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="d-flex gap-2 mt-3">
                      {record?.status !== "Lost" ? (
                        <button
                          className="btn btn-sm btn-outline-primary flex-fill"
                          onClick={() => handleProductEdit(record)}
                          data-bs-toggle="modal"
                          data-bs-target="#edit_product">
                          <i className="ti ti-edit me-1"></i>Edit
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-success flex-fill"
                          title="Recover Product"
                          data-bs-toggle="modal"
                          data-bs-target="#mark_product_lost_modal"
                          onClick={() => setSelectedLostProduct(record)}>
                          <i className="ti ti-refresh-dot me-1"></i>Recover
                        </button>
                      )}
                      {record?.status !== "Lost" && (
                        <button
                          className="btn btn-sm btn-outline-warning flex-fill"
                          title="Mark as Lost"
                          data-bs-toggle="modal"
                          data-bs-target="#mark_product_lost_modal"
                          onClick={() => setSelectedLostProduct(record)}>
                          <i className="ti ti-alert-triangle me-1"></i>Lost
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger flex-fill"
                        title="Delete Product"
                        data-bs-toggle="modal"
                        data-inert={true}
                        data-bs-target="#delete_modal"
                        onClick={() => setDeleteRecord(record)}>
                        <i className="ti ti-trash me-1"></i>Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Ant Design Style Pagination */}
      {filteredData && filteredData.length > 0 && (
        <div className="d-flex justify-content-center gap-3 align-items-center mt-4 px-2">
          {/* Left side - Total info */}
          <div className="text-dark small">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} items
          </div>

          {/* Center - Pagination Controls */}
          <div className="d-flex align-items-center">
            {totalPages > 1 && (
              <nav className="me-3">
                <ul className="pagination pagination-sm mb-0 antd-pagination">
                  {/* Previous Button */}
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}>
                    <button
                      className="page-link antd-pagination-prev"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Previous Page">
                      <i className="ti ti-chevron-left"></i>
                    </button>
                  </li>

                  {/* First page + ellipsis */}
                  {currentPage > 3 && (
                    <>
                      <li className="page-item">
                        <button
                          className="page-link antd-pagination-item"
                          onClick={() => handlePageChange(1)}>
                          1
                        </button>
                      </li>
                      {currentPage > 4 && (
                        <li className="page-item disabled">
                          <span className="page-link antd-pagination-ellipsis">
                            •••
                          </span>
                        </li>
                      )}
                    </>
                  )}

                  {/* Page Numbers */}
                  {getPageNumbers().map((pageNum) => (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        currentPage === pageNum ? "active" : ""
                      }`}>
                      <button
                        className={`page-link ${
                          currentPage === pageNum
                            ? "antd-pagination-item-active"
                            : "antd-pagination-item"
                        }`}
                        onClick={() => handlePageChange(pageNum)}>
                        {pageNum}
                      </button>
                    </li>
                  ))}

                  {/* Last page + ellipsis */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <li className="page-item disabled">
                          <span className="page-link antd-pagination-ellipsis">
                            •••
                          </span>
                        </li>
                      )}
                      <li className="page-item">
                        <button
                          className="page-link antd-pagination-item"
                          onClick={() => handlePageChange(totalPages)}>
                          {totalPages}
                        </button>
                      </li>
                    </>
                  )}

                  {/* Next Button */}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}>
                    <button
                      className="page-link antd-pagination-next"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      title="Next Page">
                      <i className="ti ti-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!filteredData || filteredData.length === 0) && (
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="ti ti-package-off display-1 text-muted"></i>
              </div>
              <h5 className="text-muted">No Products Found</h5>
              <p className="text-muted">
                There are no products to display at the moment.
              </p>
              <button
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#add_products">
                <i className="ti ti-plus me-2"></i>Add First Product
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetProductGrid;
